import { Component } from '@angular/core';
import { ActivatedRoute, ActivationStart, NavigationEnd, Router } from '@angular/router';
import { UserService } from './services/user.service';
import { FeatureFlagsService } from './services/feature-flags.service';
import { AuthService } from './services/auth.service';
import { APP_VERSION, ROUTE_APPT, ROUTE_TASK, ROUTE_VACCINE, ROUTE_VIEW, ROUTE_VISIT, SSO_CREDENTIAL } from '@string';
import * as AppConstant from './app.string';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { GoogleTagsService } from './services/google-tags.service';
import { environment } from '@env/environment';
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent {
  title = 'patient-portal';
  public loaded = false;
  public featuresInit = false;
  public showNpsRating: boolean = false;
  public initalCheckNpsCompleted: boolean = false;
  public checkingNpsRating: boolean = false;
  public npsOptions: any;

  // Optimization: Track core data loading separately
  private coreDataLoaded = false;
  private languagesLoaded = false;

  // Token refresh tracking
  private tokenRefreshCompleted = false;
  private signinCompleted = false;
  private isSSORedirection = false;

  constructor(public user: UserService, public router: Router, private featureFlagService: FeatureFlagsService, private gtm: GoogleTagManagerService, private gtmService: GoogleTagsService, private route: ActivatedRoute, private authService: AuthService) {
    // Call updateInfo immediately on app initialization to ensure it's the first API call
    this.initializeApp();

    // Fallback: Ensure loader is dismissed after maximum timeout to prevent infinite loading
    this.setLoaderFallbackTimeout();

    this.router.events.subscribe(events => {
      if (events instanceof NavigationEnd || events instanceof ActivationStart) {
        let isPatientTask = false;
        if (this.router.url.includes(ROUTE_TASK) || this.router.url.includes(ROUTE_VACCINE) || this.router.url.includes(`/${ROUTE_VIEW}`) || this.router.url.includes(ROUTE_VISIT)) {
          this.user.setPatientTask(true);
          isPatientTask = true;
        }

        // Optimize: Only call initConfig once
        if (!this.loaded) {
          this.initConfig(isPatientTask)
        }

        // Optimize: Set up feature flags subscription only once and handle both cases
        if (!isPatientTask && !this.featuresInit) {
          this.featureFlagService.rxjsFeatureInitSubject.subscribe(_ => {
            this.featuresInit = true;
          })
        } else if (isPatientTask && !this.featuresInit) {
          // For patient tasks, features are initialized immediately
          this.featuresInit = true;
        }
      }
    });
    const focus = (event) => {
      document['_activeElement'] = event.target
    }

    document.addEventListener('click', focus);
    document.addEventListener('focus', focus);
  }

  private initializeApp() {
    // Check for refresh token in URL and store it before making API calls
    this.handleRefreshTokenFromURL();

    // Optimized: Call getUpdateInfo and handle core data loading
    this.user.getUpdateInfo('*').subscribe(
      data => {
        console.log('getUpdateInfo appcomponent - first call', data, this.route.snapshot.queryParams)
        if (data?.configuration?.languages && this.route.snapshot.queryParams?.lang) {
          this.user.setDefaultLanguage(data.configuration.languages, this.route.snapshot.queryParams.lang)
        }

        // Mark core data as loaded immediately after first successful API call
        this.coreDataLoaded = true;
        this.checkReadyState();
      },
      error => {
        console.error('Failed to load core data:', error);
        // Even on error, allow the app to continue with cached data
        this.coreDataLoaded = true;
        this.checkReadyState();
      }
    )
  }

  /**
   * Check for refresh token in URL and store it in localStorage
   * Handles both Angular routing and raw URL parsing for hash-based routing
   */
  private handleRefreshTokenFromURL(): void {
    try {
      const sso = this.route.snapshot.queryParams?.sso;
      if (sso == 'Y') { 
        this.isSSORedirection = true;
        return;
      }
      const refreshToken = this.route.snapshot.queryParams?.rt || this.extractTokenFromURL();
      
      console.log('refreshToken', refreshToken, 'URL:', window.location.href);
      
      if (refreshToken) {
        console.log('Refresh token found - storing and cleaning URL');
        localStorage.setItem('SSO_REFRESH_TOKEN', refreshToken);
        this.cleanTokenFromURL();
      } else {
        console.log('No refresh token found in URL');
      }
    } catch (error) {
      console.error('Error handling refresh token from URL:', error);
    }
  }

  /**
   * Extract refresh token from URL using optimized regex
   * Handles all URL patterns in a single efficient check
   */
  private extractTokenFromURL(): string | null {
    const match = window.location.href.match(/[?&#].*?[?&]rt=([^&]+)/);
    return match?.[1] ? decodeURIComponent(match[1]) : null;
  }

  /**
   * Remove refresh token parameter from URL efficiently
   * Uses single regex replacement for all cases
   */
  private cleanTokenFromURL(): void {
    try {
      const cleanUrl = window.location.href
        .replace(/([?&#].*?)([?&])rt=[^&]*(&?)/g, (_, prefix, separator, suffix) => {
          // If rt is the only param after ? or #, remove the separator
          if (separator === '?' && !suffix) return prefix;
          // If rt has params after it, keep the separator
          if (suffix) return prefix + separator;
          // If rt is last param after &, remove it entirely
          return prefix;
        })
        .replace(/[?&]$/, ''); // Clean trailing separators

      if (cleanUrl !== window.location.href) {
        window.history.replaceState({}, document.title, cleanUrl);
      }
    } catch (error) {
      console.error('Error cleaning token from URL:', error);
    }
  }

  private getNpsRating() {
    if (!this.user.isLoggedIn() || this.user.isNpsModalDisplayed() || this.checkingNpsRating) {
      return;
    }
    this.checkingNpsRating = false
    this.initalCheckNpsCompleted = true;
    this.user.showNpsRatingControl().subscribe(data => {
      this.checkingNpsRating = false
      if (data.showRating) {
        this.showNpsRatingModal(data.options)
      }
    }, err => {
      this.checkingNpsRating = false
    })
  }

  private showNpsRatingModal(options) {
    this.npsOptions = options
    this.showNpsRating = true;
  }

  public closeNpsRating(data) {
    this.showNpsRating = false;
    this.user.storeNpsRating(data.action || 'SKIP', data.values || {}, this.npsOptions).subscribe(response => {
      console.log(response)
    })
  }

  /**
   * Optimized loader management: Hide loader after core data is loaded and signin is completed
   * This ensures the user sees a complete authenticated state before the loader disappears
   */
  private checkReadyState() {
    // Check if both core data and languages are loaded to initiate token refresh
    if (this.coreDataLoaded && this.languagesLoaded && !this.tokenRefreshCompleted) {
      console.log('Core data and languages loaded - checking for token refresh');
      this.checkAndPerformTokenRefresh();
    }

    // Hide loader only after core data is loaded AND signin is completed (or no signin needed)
    if (this.coreDataLoaded && this.signinCompleted && !this.loaded) {
      console.log('Core data loaded and signin completed - dismissing prebootstrap loader');
      this.loaded = true;
    }
  }

  /**
   * Check for stored refresh token and perform signin if available
   * This replaces the APP_INITIALIZER logic with proper timing
   */
  private checkAndPerformTokenRefresh() {
    this.tokenRefreshCompleted = true; // Mark as attempted to prevent multiple calls

    if (!this.hasStoredCredentials() || this.isSSORedirection) {
      console.log('No stored credentials found - skipping token refresh');
      this.signinCompleted = true; // No signin needed
      this.checkReadyState(); // Check if we can now hide the loader
      return;
    }

    console.log('Stored credentials found - initiating token refresh');
    this.authService.initializeTokenRefresh().subscribe({
      next: (success: boolean) => {
        if (success) {
          console.log('Token refresh successful - user authenticated');
        } else {
          console.warn('Token refresh failed - user may need to login again');
        }
        this.signinCompleted = true; // Mark signin as completed (success or failure)
        this.checkReadyState(); // Check if we can now hide the loader
      },
      error: (error) => {
        console.error('Token refresh error:', error);
        this.signinCompleted = true; // Mark signin as completed even on error
        this.checkReadyState(); // Check if we can now hide the loader
      }
    });
  }

  /**
   * Check if we have the necessary credentials stored for token refresh
   * Only refresh token is required, SSO credentials are optional
   */
  private hasStoredCredentials(): boolean {
    try {
      const refreshToken = localStorage.getItem('SSO_REFRESH_TOKEN');

      // Only check for refresh token - SSO credentials are optional
      return !!refreshToken;
    } catch (error) {
      console.error('Error checking stored credentials:', error);
      return false;
    }
  }

  /**
   * Fallback mechanism to ensure loader is never stuck indefinitely
   * This prevents poor user experience due to network issues or API failures
   */
  private setLoaderFallbackTimeout() {
    setTimeout(() => {
      if (!this.loaded) {
        console.warn('Loader fallback timeout triggered - forcing loader dismissal');
        this.loaded = true;
        this.coreDataLoaded = true;
        this.languagesLoaded = true;
        this.signinCompleted = true; // Force signin as completed to allow loader dismissal

        // Try to initialize features if not already done
        if (!this.featuresInit) {
          this.featuresInit = true;
        }

        // Also trigger token refresh if not completed yet
        if (!this.tokenRefreshCompleted) {
          this.checkAndPerformTokenRefresh();
        }
      }
    }, 10000); // 10 second maximum loader time
  }

  private initConfig(isPatientTask) {
    if (!isPatientTask) {
      // Optimize: Load masters in background, don't block the UI
      this.user.setMasters()

      // Optimize: Load languages asynchronously and track completion
      this.user.getLanguages().subscribe(
        () => {
          this.languagesLoaded = true;
          this.checkReadyState();
        },
        () => {
          // Even on error, mark as loaded to prevent blocking
          this.languagesLoaded = true;
          this.checkReadyState();
        }
      );

      console.log('npsRatingNoSubject')
      if (this.user.canViewOption('SHOWNPSRATINGINPXPORTAL') && !this.initalCheckNpsCompleted) {
        this.getNpsRating()
      }
      this.user.npsRatingSubject.subscribe(_ => {
        console.log('npsRatingSubject')
        if (this.user.canViewOption('SHOWNPSRATINGINPXPORTAL')) {
          this.getNpsRating()
        }
      })
    } else {
      this.user.getConfigurationInfo.subscribe(data => {
        if (data['profile'] && data['profile']['googleMapApiKey']) {
          let key = data['profile']['googleMapApiKey'];
          this.user.setGoogleApiKey(key);
        }
      });
      this.featuresInit = true;
      // For patient tasks, set loaded immediately
      this.loaded = true;
    }
  }
  ngOnInit(): void {
    //Called after the constructor, initializing input properties, and the first call to ngOnChanges.
    //Add 'implements OnInit' to the class.
    this.gtm.addGtmToDom();
    this.user.getUserId().subscribe(userId => {
      const obj = {
        event_source: 'Website',
        language: this.user.currentLanguage,
        'user_id': userId
      }
      this.gtmService.pushGTMEvent(null, 'global_variables', obj);
    })
    let hash = window.location.hash;
    if (environment['COOKIESCRIPT'] && environment['COOKIESCRIPT'].enable && hash.includes('#/portal')) {
      const lacaleBasedInfo = environment['COOKIESCRIPT']
      setTimeout(() => {
        document.querySelector('html')?.setAttribute('lang', this.user.currentLanguage)
      }, 1500)
      const script = document.createElement('script');

      lacaleBasedInfo['directAttributes'].forEach(element => {
        script[element.key] = element.value
      });
      lacaleBasedInfo['setAttributes'].forEach(element => {
        script.setAttribute(element.key, element.value);
      });
      const parent_node = document.head || document.body;
      parent_node.appendChild(script);
      if (lacaleBasedInfo['onloadFunctions']) {
        script.addEventListener('load', () => {
          console.log('script load', window);
          lacaleBasedInfo['onloadFunctions'].forEach(element => {
            switch (element) {
              case 'setConsentBannerParams':
                if (lacaleBasedInfo['supportedDomain']) {
                  let mappedDomain = lacaleBasedInfo['supportedDomain'].reduce((acc, val) => {
                    acc[val] = this.user.currentLanguage
                    return acc
                  }, {});
                  (window as any).setConsentBannerParams({ overrideBannerLanguage: mappedDomain });
                }
                break;
              default:
                (window as any)[element]();
                break;
            }
          })

        });
      }
    }
  }


  ngAfterContentInit() {
    if (environment['enableHotJor']) {
      ((h, o, t, j, a, r) => {
        h.hj =
          h.hj ||
          function () {
            (h.hj.q = h.hj.q || []).push(arguments);
          };
        h._hjSettings = { hjid: environment['hotJorKey'], hjsv: 6 };
        a = o.getElementsByTagName('head')[0];
        r = o.createElement('script');
        r.async = 1;
        r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
        a.appendChild(r);
      })(window as any, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=');
      console.log('hotjor init', environment['hotJorKey'])
    }

  }

}
